import { useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchStoreSettings } from "../api/config";
import { useAuth } from "../auth/AuthContext";
import type { Membership } from "../api/types";

function tierGradient(color: string | null | undefined): string {
  if (!color || !/^#[0-9a-fA-F]{6}$/.test(color)) {
    return "linear-gradient(160deg, #e8d8c4 0%, #f5ede3 100%)";
  }
  return `linear-gradient(160deg, ${color}55 0%, ${color}1a 100%)`;
}

function tierAccent(color: string | null | undefined): string {
  return color && /^#[0-9a-fA-F]{6}$/.test(color) ? color : "#8B6F47";
}

function Divider() {
  return <Box sx={{ height: "1px", background: "#E8E0D8", mx: 0, my: "12px" }} />;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#9A8A7A", letterSpacing: "1.2px", textTransform: "uppercase", mb: "8px" }}>
      {children}
    </Typography>
  );
}

function BenefitItem({ label, color }: { label: string; color: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "8px", mb: "7px" }}>
      <Box sx={{
        mt: "2px", width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
        background: color, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <svg viewBox="0 0 10 10" width="9" height="9" fill="none">
          <path d="M2 5l2 2 4-4" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </Box>
      <Typography sx={{ fontSize: "13px", color: "#3A3A3A", lineHeight: 1.45, flex: 1 }}>{label}</Typography>
    </Box>
  );
}

function TierCard({ tier, isCurrentTier, nextTier }: { tier: Membership; isCurrentTier: boolean; nextTier?: Membership }) {
  const accent = tierAccent(tier.color);
  const gradient = tierGradient(tier.color);

  const benefits: string[] = [];
  if (tier.discountPercentage > 0) {
    benefits.push(`${tier.discountPercentage}% member discount on all purchases`);
  }
  if (tier.description) {
    // split multi-line descriptions into separate items
    tier.description.split(/[.\n]/).map(s => s.trim()).filter(Boolean).forEach(s => benefits.push(s));
  }
  if (benefits.length === 0) {
    benefits.push("Exclusive member benefits");
    benefits.push("Member-exclusive coupons");
  }

  const requirementText = tier.minSpendAmount <= 0
    ? "Available upon new registration"
    : `Accumulate HK$${tier.minSpendAmount.toLocaleString()} in a single period`;

  const maintainText = tier.minSpendAmount <= 0
    ? "No minimum spend required to retain this tier"
    : `Spend HK$${tier.minSpendAmount.toLocaleString()} per period to retain status`;

  return (
    <Box sx={{
      width: "calc(100vw - 56px)",
      maxWidth: "310px",
      flexShrink: 0,
      borderRadius: "20px",
      overflow: "hidden",
      border: isCurrentTier ? `2px solid ${accent}` : "1.5px solid #DDD5CA",
      boxShadow: isCurrentTier
        ? `0 6px 24px ${accent}40`
        : "0 2px 14px rgba(0,0,0,0.08)",
      background: "#fff",
      scrollSnapAlign: "center",
    }}>
      {/* Header with gradient */}
      <Box sx={{ background: gradient, px: "18px", pt: "18px", pb: "16px" }}>
        {isCurrentTier && (
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: "#fff", borderRadius: "99px", px: "10px", py: "4px",
            mb: "10px", boxShadow: "0 1px 6px rgba(0,0,0,0.12)",
          }}>
            <svg viewBox="0 0 12 12" width="10" height="10" fill={accent}>
              <path d="M6 1.5l1.1 2.3 2.5.35-1.8 1.75.43 2.5L6 7.2l-2.23 1.2.43-2.5L2.4 4.15l2.5-.35L6 1.5z"/>
            </svg>
            <Typography sx={{ fontSize: "10px", fontWeight: 700, color: accent, letterSpacing: "0.4px", textTransform: "uppercase" }}>
              Your Tier
            </Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: "10px", fontWeight: 700, color: accent, letterSpacing: "1.5px", textTransform: "uppercase", mb: "3px" }}>
          {tier.name.toUpperCase()}
        </Typography>
        <Typography sx={{ fontSize: "24px", fontWeight: 800, color: "#1A1A1A", letterSpacing: "-0.5px", lineHeight: 1.2 }}>
          {tier.nameZh ? tier.nameZh : `${tier.name} Member`}
        </Typography>
        {(tier.descriptionZh || tier.description) && (
          <Typography sx={{ mt: "6px", fontSize: "12px", color: "#5A5A5A", lineHeight: 1.5 }}>
            {tier.descriptionZh || tier.description}
          </Typography>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ px: "18px", pt: "14px", pb: "18px" }}>
        <SectionLabel>Member Benefits</SectionLabel>
        {benefits.map((b, i) => <BenefitItem key={i} label={b} color={accent} />)}

        <Divider />

        <SectionLabel>Requirements</SectionLabel>
        <Typography sx={{ fontSize: "13px", color: "#5A5A5A", lineHeight: 1.5, mb: "12px" }}>{requirementText}</Typography>

        <SectionLabel>Maintain Status</SectionLabel>
        <Typography sx={{ fontSize: "13px", color: "#5A5A5A", lineHeight: 1.5 }}>{maintainText}</Typography>

        {nextTier && (
          <>
            <Divider />
            <SectionLabel>Upgrade to {nextTier.name}</SectionLabel>
            <Typography sx={{ fontSize: "13px", color: tierAccent(nextTier.color), lineHeight: 1.5, fontWeight: 500 }}>
              Accumulate HK${nextTier.minSpendAmount.toLocaleString()} in a single period to reach {nextTier.name}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}

export default function MemberPrivilegesPage() {
  const navigate = useNavigate();
  const { customer } = useAuth();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const { data: storeSettings, isLoading, isError } = useQuery({
    queryKey: ["store-settings"],
    queryFn: fetchStoreSettings,
    staleTime: 10 * 60_000,
  });

  const tiers: Membership[] = (storeSettings?.memberships ?? [])
    .filter((m) => m.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const currentTierName = (customer?.membershipLevelName ?? "").toLowerCase();

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || tiers.length === 0) return;
    const cardWidth = el.scrollWidth / tiers.length;
    setActiveIndex(Math.round(el.scrollLeft / cardWidth));
  };

  return (
    <Box sx={{ flex: 1, backgroundColor: "#F0EBE3", display: "flex", flexDirection: "column", pb: "30px" }}>
      {/* Sticky header */}
      <Box sx={{
        backgroundColor: "#fff", borderBottom: "1px solid #EAEAEA",
        display: "flex", alignItems: "center", px: 2, pt: 2, pb: "14px", gap: 1.5,
        position: "sticky", top: 0, zIndex: 10,
      }}>
        <Box
          component="button"
          onClick={() => navigate(-1)}
          sx={{
            width: 36, height: 36, border: "none", background: "none",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            color: "#3A3A3A", borderRadius: "50%", "&:active": { background: "#F0F0F0" },
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Box>
        <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#1A1A1A" }}>
          Member Privileges
        </Typography>
      </Box>

      {/* Current tier summary */}
      {customer?.membershipLevelName && (
        <Box sx={{ px: 2, pt: "16px" }}>
          <Box sx={{
            background: "#fff", borderRadius: "16px", p: "14px 16px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
            display: "flex", alignItems: "center", gap: "12px",
          }}>
            <Box sx={{
              width: 42, height: 42, borderRadius: "50%", flexShrink: 0,
              background: "linear-gradient(145deg, #7BA88A, #4E8A65)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                <path d="M12 3.5L19.5 12 12 20.5 4.5 12 12 3.5z" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M12 7.5L16.2 12 12 16.5 7.8 12 12 7.5z" stroke="#fff" strokeWidth="1.4" strokeLinejoin="round"/>
              </svg>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "11px", color: "#8A8A8A", fontWeight: 500 }}>Your current tier</Typography>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#1A1A1A" }}>{customer.membershipLevelName}</Typography>
            </Box>
            {(customer.nextMembershipLevel?.requiredSpendingAmountToNextLevel ?? 0) > 0 && (
              <Typography sx={{ fontSize: "12px", color: "#7BA88A", fontWeight: 600, textAlign: "right" }}>
                HK${customer.nextMembershipLevel!.requiredSpendingAmountToNextLevel.toLocaleString()}{"\n"}to next tier
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Section title */}
      <Box sx={{ px: 2, pt: "20px", pb: "12px" }}>
        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#9A8A7A", letterSpacing: "1.2px", textTransform: "uppercase" }}>
          Membership Tiers
        </Typography>
      </Box>

      {/* Horizontal carousel */}
      {isLoading ? (
        <Box sx={{ display: "flex", gap: "12px", px: "20px", overflowX: "hidden" }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ width: "calc(100vw - 56px)", maxWidth: 310, flexShrink: 0, borderRadius: "20px", background: "#E8E0D8", height: 380, opacity: 0.5 + i * 0.1 }} />
          ))}
        </Box>
      ) : isError || tiers.length === 0 ? (
        <Box sx={{ mx: 2, background: "#fff", borderRadius: "16px", p: "24px", textAlign: "center" }}>
          <Typography sx={{ fontSize: "14px", color: "#8A8A8A" }}>
            {isError ? "Unable to load membership tiers." : "No membership tiers available."}
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            ref={scrollRef}
            onScroll={handleScroll}
            sx={{
              display: "flex",
              gap: "12px",
              px: "20px",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              pb: "4px",
            }}
          >
            {tiers.map((tier, i) => {
              const isCurrentTier = currentTierName.length > 0 &&
                (currentTierName.includes(tier.name.toLowerCase()) || tier.name.toLowerCase().includes(currentTierName));
              const nextTier = tiers[i + 1];
              return <TierCard key={tier.id} tier={tier} isCurrentTier={isCurrentTier} nextTier={nextTier} />;
            })}
            {/* trailing spacer so last card can center */}
            <Box sx={{ flexShrink: 0, width: "8px" }} />
          </Box>

          {/* Dot indicators */}
          {tiers.length > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", gap: "6px", pt: "16px" }}>
              {tiers.map((_, i) => (
                <Box
                  key={i}
                  component="button"
                  onClick={() => {
                    const el = scrollRef.current;
                    if (!el) return;
                    const cardWidth = el.scrollWidth / tiers.length;
                    el.scrollTo({ left: cardWidth * i, behavior: "smooth" });
                  }}
                  sx={{
                    width: i === activeIndex ? 20 : 7,
                    height: 7,
                    borderRadius: "99px",
                    background: i === activeIndex ? "#8B6F47" : "#C8B8A8",
                    transition: "width 0.2s ease, background 0.2s ease",
                    border: "none", padding: 0, cursor: "pointer",
                  }}
                />
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
